import EventEmitter from '../common/EventEmitter';
import InterceptorStack from './InterceptorStack';
import HttpInterceptor from './HttpInterceptor';
import { PlayType, SourceType } from '../common/constants';
import { Item } from '../webrtc/agent/interfaces';
import ContentConfig from '../config/ContentConfig';
import maybeModifyManifest from './maybeModifyManifest';
import ProxyInterceptor from './ProxyInterceptor';
import { InternalEvents } from '../events';
import { ReporterState } from '../state/LocalState';
import { OidFunction } from '@kollective-frontend/ksdk-api';

interface InterceptorStackArgs {
  items: Item[];
  sourceType: SourceType;
  playType: PlayType;
  config: ContentConfig;
  createObjectId?: OidFunction;
  sdkVer: string;
  sessionToken: string;
  tenantId: string;
  contentId: string;
  changeHandler?: {
    state: ReporterState;
    emitter: EventEmitter;
  };
}

export default function createInterceptorStack({
  items,
  sourceType,
  playType,
  config,
  createObjectId,
  sdkVer,
  sessionToken,
  tenantId,
  contentId,
  changeHandler
}: InterceptorStackArgs): InterceptorStack {
  const shouldReport = typeof changeHandler !== 'undefined';
  if (changeHandler) {
    const { state, emitter } = changeHandler;
    // Update state when a interceptor is added or removed
    emitter.addEventListener(InternalEvents.INTERCEPTOR_CHANGE, evt => {
      if (evt && evt.data) {
        const { add, name } = evt.data;
        if (add) {
          state.interceptors.unshift(name);
        } else {
          const list = state.interceptors;
          state.interceptors = list.filter(i => i !== name);
        }
      }
    });
  }
  const stack = new InterceptorStack(config, shouldReport, createObjectId);
  const http = new HttpInterceptor(config);

  // Register mapper for dealing with manifests for all interceptors.
  stack.registerResponseMapper(response => {
    const config = ContentConfig.getInstance();

    const mapper = maybeModifyManifest(
      items,
      config.playback,
      sourceType,
      playType
    );

    return mapper(response);
  });

  // add http interceptor to stack as base interceptor.
  stack.putInterceptor(http);

  if (config.proxy && !config.proxy.disabled) {
    const proxy = new ProxyInterceptor({
      config,
      playType,
      sdkVer,
      sessionToken,
      tenantId,
      contentId
    });
    stack.putInterceptor(proxy);
  }

  return stack;
}
